/* ============================================================
   SORRY NONNA - Shopify AJAX cart (drawer + /cart page)
   Intercepts add-to-cart forms -> /cart/add.js, refreshes the
   drawer from /cart.js, opens it. Quantity/remove controls on
   both the drawer and the cart page go through queueChange().
   Server-rendered HTML is the no-JS fallback on both.

   Responsiveness (#197). Quantity controls used to wait for the
   network before anything moved - and on /cart they triggered a
   full page reload. The rules now:
     1. The number changes on click. Always. The request follows.
     2. Clicks are keyed by line item KEY, never by line index -
        an index shifts the moment another line is removed, so a
        pending change could land on the wrong product.
     3. Rapid clicks accumulate into ONE request per line, sent
        after a short pause, and only one request is in flight at
        a time. The cart is a single server-side resource, so
        parallel writes come back out of order.
     4. Re-render patches the existing rows in place. Rebuilding
        innerHTML threw away scroll position and focus and forced
        the browser to re-decode every product image.
   ============================================================ */
(function () {
  "use strict";

  function money(c) { return (window.SN && window.SN.money) ? window.SN.money(c) : "$" + (c / 100).toFixed(2); }

  /* Shop-editable strings reach this file through data attributes and get concatenated into
     innerHTML, so they are escaped on the way in. Liquid's | escape handles the attribute; this
     handles the re-insertion. An apostrophe in the copy is enough to matter. */
  function escapeHtml(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  /* /cart.js hands back the ORIGINAL image url with no size parameters - for these products that
     is a 2000x2500 file (up to ~860KB) being poured into a 64px box. Measured against the CDN:
     the same image at width=128 is ~8.5KB. Ask the CDN for the size we actually draw. */
  function sizedImage(url, w) {
    if (!url) return "";
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + w;
  }

  /* Bin icon for the remove control - must match the SVG in snippets/cart-drawer.liquid so the
     server HTML and this AJAX re-render look identical. Lid is a separate <g> so CSS can flip it open. */
  var BIN_SVG =
    '<svg class="cart-line__bin" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<g class="cart-line__bin-lid">' +
        '<line x1="4.5" y1="6.5" x2="19.5" y2="6.5"></line>' +
        '<path d="M9.5 6.5V5A1.5 1.5 0 0 1 11 3.5h2A1.5 1.5 0 0 1 14.5 5v1.5"></path>' +
      "</g>" +
      '<path d="M6.5 8l.8 10.6A1.8 1.8 0 0 0 9.1 20.3h5.8a1.8 1.8 0 0 0 1.8-1.7L17.5 8"></path>' +
      '<line x1="10.2" y1="10.8" x2="10.4" y2="17.3"></line>' +
      '<line x1="13.8" y1="10.8" x2="13.6" y2="17.3"></line>' +
    "</svg>";

  function getCart() {
    return fetch("/cart.js", { headers: { "Accept": "application/json" } }).then(function (r) { return r.json(); });
  }

  /* The cart as the shopper SEES it (#225): ordinary lines pass through untouched, and the jar lines
     of a pack (same _pack property, added by snippets/pack-add-fields.liquid) fold into one entry
     at the position of the first jar. The entry's key is "pack:" + the jar keys comma-joined in
     cart order - character for character what snippets/cart-pack-line.liquid writes, so the in-place
     patch below recognises server-drawn pack rows. Quantity is the smallest jar count (complete
     packs); prices are the true sums, so the row always totals what checkout will charge. */
  var PACK = "pack:";
  function cartLines(items) {
    var out = [], groups = Object.create(null);
    (items || []).forEach(function (it) {
      var pk = it.properties && it.properties._pack;
      if (!pk) { out.push(it); return; }
      var g = groups[pk];
      if (!g) {
        g = groups[pk] = { pack: pk, keys: [], items: [], quantity: Infinity, final_line_price: 0,
          original_line_price: 0, url: it.properties._pack_url || it.url };
        out.push(g);
      }
      g.keys.push(it.key);
      g.items.push(it);
      g.quantity = Math.min(g.quantity, it.quantity);
      g.final_line_price += it.final_line_price;
      g.original_line_price += it.original_line_price;
    });
    out.forEach(function (l) { if (l.pack) l.key = PACK + l.keys.join(","); });
    return out;
  }

  /* ----------------------------------------------------------
     Write queue
     ----------------------------------------------------------
     pending  = { lineItemKey: targetQuantity } - the quantity the shopper has asked for but
                that the server has not confirmed yet. Clicking + three times fast writes 4 here
                and sends one request, instead of sending "2" three times (the old bug: each
                button carried a quantity baked in at render time, so the 2nd and 3rd click both
                re-sent the value from before the 1st, and two of the three clicks vanished).
     inFlight = guard so writes go one at a time, in order. */
  var pending = Object.create(null);
  var flushTimer = null;
  var inFlightKey = null;
  var FLUSH_DELAY = 220;   /* long enough to swallow a double-tap, short enough to feel instant */

  /* A line the shopper has changed but the server has not confirmed. Its on-screen number is
     ahead of the response we are currently rendering, so render() must leave it alone -
     otherwise changing line A then line B makes B visibly snap back when A's response lands. */
  function isPending(key) { return key === inFlightKey || pending[key] !== undefined; }

  function queueChange(key, qty) {
    if (!key) return;
    pending[key] = Math.max(0, qty);
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, FLUSH_DELAY);
  }

  function flush() {
    flushTimer = null;
    if (inFlightKey) return;                    /* the in-flight request re-arms this when it lands */
    var key = Object.keys(pending)[0];
    if (!key) return;
    var qty = pending[key];
    delete pending[key];
    inFlightKey = key;

    /* A pack row sets every one of its jar lines to the same quantity in ONE request, so the
       cart never sits half a pack out of step (#225). /cart/update.js takes line keys too. */
    var req;
    if (key.indexOf(PACK) === 0) {
      var updates = {};
      key.slice(PACK.length).split(",").forEach(function (k) { updates[k] = Number(qty); });
      req = { url: "/cart/update.js", body: { updates: updates } };
    } else {
      /* id: <line item key>, not line: <index>. The key survives other lines being removed. */
      req = { url: "/cart/change.js", body: { id: key, quantity: Number(qty) } };
    }

    fetch(req.url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(req.body)
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, cart: d }; }); })
      .then(function (res) {
        inFlightKey = null;
        /* A rejected change (asking for more than is in stock is the common one) answers with an
           error object, not a cart. Drawing it would blow up on cart.items - go and get the real
           cart instead, so the number on screen falls back to what actually happened. */
        if (!res.ok || !res.cart || !res.cart.items) {
          var why = res.cart && (res.cart.description || res.cart.message);
          if (why) console.warn("Cart update rejected:", why);
          return getCart().then(render).catch(function () {});
        }
        render(res.cart);
      })
      .catch(function (e) {
        inFlightKey = null;
        console.error("Cart update failed:", e);
        /* Put the shopper back on solid ground: whatever we drew optimistically may be wrong. */
        return getCart().then(render).catch(function () {});
      })
      .then(function () {
        if (Object.keys(pending).length) flush();   /* more queued while we waited */
      });
  }

  /* Optimistic paint. Runs before the request, so the number moves on the same frame as the tap.
     Returns the new quantity so the caller can queue it. */
  function bumpLine(row, delta, numSel) {
    var numEl = row.querySelector(numSel);
    var current = numEl ? parseInt(numEl.textContent, 10) : NaN;
    if (isNaN(current)) return null;
    var next = Math.max(0, current + delta);
    if (numEl) numEl.textContent = next;
    /* The line total and the subtotal cannot be recalculated here - volume discounts are the
       server's call (#184) - so mark them stale rather than showing a number we invented. This
       covers the struck-through was-price too: crossing 4 jars is exactly when it appears or
       disappears, and that is the server's answer to give, not ours to guess. */
    row.classList.add("is-syncing");
    return next;
  }

  /* ----------------------------------------------------------
     Drawer render
     ---------------------------------------------------------- */
  /* The line price, with what it would have cost struck through above it when the 4+ jar discount
     is applying. Both figures come from the server's cart - the saving is a Shopify automatic
     discount, so the browser is never the one deciding whether there is one. Mirrors the Liquid in
     snippets/cart-drawer.liquid character for character; if one changes, change both. */
  function linePriceHtml(it) {
    var was = "";
    if (it.original_line_price > it.final_line_price) {
      was = '<s class="cart-line__was">' + money(it.original_line_price) + "</s>";
    }
    return was + money(it.final_line_price);
  }

  /* Pack row for the drawer - mirrors snippets/cart-pack-line.liquid (surface 'drawer'). Controls
     point at /cart as their no-JS fallback, since one link cannot change several lines. */
  function drawerPackHtml(g) {
    var key = escapeHtml(g.key);
    var grid = g.items.map(function (it) {
      return it.image ? '<img src="' + sizedImage(it.image, 64) + '" alt="" width="32" height="32" loading="lazy">' : "";
    }).join("");
    var names = g.items.map(function (it) { return escapeHtml(it.product_title); }).join(", ");
    var url = g.url || "#";
    return '<a class="cart-line__img-link" href="' + url + '"><span class="cart-line__img cart-pack-grid">' + grid + "</span></a>" +
      '<div class="cart-line__body"><a class="cart-line__name" href="' + url + '">' + escapeHtml(g.pack) + "</a>" +
      '<div class="cart-line__meta">' + g.items.length + " jars: " + names + "</div>" +
      '<div class="cart-line__qty">' +
        '<a href="/cart" data-cart-key="' + key + '" data-cart-delta="-1" aria-label="Decrease">&minus;</a>' +
        '<span data-cart-qty>' + g.quantity + "</span>" +
        '<a href="/cart" data-cart-key="' + key + '" data-cart-delta="1" aria-label="Increase">+</a>' +
      "</div></div>" +
      '<div class="cart-line__aside">' +
        '<a class="cart-line__remove" href="/cart" data-cart-key="' + key + '" data-cart-remove aria-label="Remove ' + escapeHtml(g.pack) + '">' + BIN_SVG + "</a>" +
        '<div class="cart-line__price" data-cart-line-price>' + linePriceHtml(g) + "</div>" +
      "</div>";
  }

  function drawerLineHtml(it, line) {
    if (it.pack) return drawerPackHtml(it);
    var variant = (it.variant_title && it.variant_title.indexOf("Default") === -1)
      ? '<div class="cart-line__meta">' + escapeHtml(it.variant_title) + "</div>" : "";
    var url = it.url || "#";
    var imgInner = it.image
      ? '<img class="cart-line__img" src="' + sizedImage(it.image, 128) + '" alt="" width="64" height="64" loading="lazy">'
      : '<span class="cart-line__img"></span>';
    /* Hrefs mirror the Liquid fallback so the two renders stay identical; JS intercepts them. */
    /* By key, not line index: with packs folded into one row (#225), a row's position on screen is
       no longer its position in the cart. */
    var changeUrl = "/cart/change?id=" + encodeURIComponent(it.key) + "&quantity=";
    return '<a class="cart-line__img-link" href="' + url + '">' + imgInner + "</a>" +
      '<div class="cart-line__body"><a class="cart-line__name" href="' + url + '">' + escapeHtml(it.product_title) + "</a>" + variant +
      '<div class="cart-line__qty">' +
        /* &minus; not a hyphen, to match the Liquid render character for character */
        '<a href="' + changeUrl + (it.quantity - 1) + '" data-cart-key="' + escapeHtml(it.key) + '" data-cart-delta="-1" aria-label="Decrease">&minus;</a>' +
        '<span data-cart-qty>' + it.quantity + "</span>" +
        '<a href="' + changeUrl + (it.quantity + 1) + '" data-cart-key="' + escapeHtml(it.key) + '" data-cart-delta="1" aria-label="Increase">+</a>' +
      "</div></div>" +
      '<div class="cart-line__aside">' +
        '<a class="cart-line__remove" href="' + changeUrl + '0" data-cart-key="' + escapeHtml(it.key) + '" data-cart-remove aria-label="Remove ' + escapeHtml(it.product_title) + '">' + BIN_SVG + "</a>" +
        '<div class="cart-line__price" data-cart-line-price>' + linePriceHtml(it) + "</div>" +
      "</div>";
  }

  function renderDrawer(cart) {
    var wrap = document.querySelector(".cart-drawer__items");
    if (!wrap) return;

    var foot = document.querySelector(".cart-drawer__foot");
    var ship = document.querySelector(".cart-ship");
    var upsell = document.querySelector(".cart-upsell");

    if (cart.item_count === 0) {
      wrap.innerHTML = '<p class="cart-drawer__empty">Your basket is empty.<br>Nonna would be appalled.</p>';
      if (foot) foot.style.display = "none";
      if (ship) ship.style.display = "none";
      if (upsell) upsell.style.display = "none";
      return;
    }
    if (foot) foot.style.display = "";
    if (upsell) upsell.style.display = "";
    if (ship) {
      ship.style.display = "";
      var threshold = Number(ship.getAttribute("data-fs-threshold")) || 0;
      var remaining = Math.max(0, threshold - cart.total_price);
      var pct = threshold > 0 ? Math.min(100, Math.round(cart.total_price * 100 / threshold)) : 0;
      var fill = ship.querySelector(".cart-ship__fill");
      var msg = ship.querySelector(".cart-ship__msg");
      if (fill) { fill.style.width = pct + "%"; }
      if (msg) {
        // Flat-rate wording comes from the data attribute (theme setting) so this string and the
        // Liquid one in snippets/cart-drawer.liquid cannot drift apart. Only shown while they are
        // still short of the threshold - once free shipping is unlocked the price is irrelevant.
        var flat = ship.getAttribute("data-flat-text") || "";
        var flatHtml = flat ? ' <span class="cart-ship__flat">' + escapeHtml(flat) + "</span>" : "";
        msg.innerHTML = remaining > 0
          ? 'Only <b>' + money(remaining) + '</b> more until <b>FREE SHIPPING</b>' + flatHtml
          : "You've unlocked <b>FREE SHIPPING</b>!";
      }
    }

    /* Patch in place when the same lines are on screen in the same order - which is the case for
       every quantity change. Only the numbers and prices move, so scroll position and keyboard
       focus survive and no image is re-decoded. Full rebuild only when the line-up changes. */
    var lines = cartLines(cart.items);   /* packs folded into one row each (#225) */
    var rows = wrap.querySelectorAll(".cart-line");
    var samePlan = rows.length === lines.length;
    if (samePlan) {
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute("data-cart-key") !== lines[i].key) { samePlan = false; break; }
      }
    }

    if (samePlan) {
      lines.forEach(function (it, i) {
        var row = rows[i];
        /* Pack hrefs stay on /cart. A line-index href would be wrong for them, and the indices of
           ordinary lines are cart positions, which packs no longer match one-to-one - so those
           fallbacks use the key instead, exactly as the cart page's Liquid does. */
        if (!it.pack) {
          row.querySelectorAll("[data-cart-delta]").forEach(function (b) {
            var d = Number(b.getAttribute("data-cart-delta"));
            b.setAttribute("href", "/cart/change?id=" + encodeURIComponent(it.key) + "&quantity=" + Math.max(0, it.quantity + d));
          });
        }
        if (isPending(it.key)) return;   /* shopper is ahead of this response - do not fight them */
        var num = row.querySelector("[data-cart-qty]");
        var price = row.querySelector("[data-cart-line-price]");
        if (num) num.textContent = it.quantity;
        /* innerHTML, not textContent: the line carries a struck-through was-price whenever the
           4+ discount applies, and setting text would silently drop it on every quantity change. */
        if (price) price.innerHTML = linePriceHtml(it);
        row.classList.remove("is-syncing");
      });
    } else {
      wrap.innerHTML = lines.map(function (it, i) {
        return '<div class="cart-line" data-cart-key="' + escapeHtml(it.key) + '">' + drawerLineHtml(it, i + 1) + "</div>";
      }).join("");
    }

    var sub = document.querySelector(".cart-drawer__subtotal .v");
    if (sub) sub.textContent = money(cart.total_price);
    /* Scoped to the drawer: on /cart both surfaces are in the DOM and carry the same hooks. */
    applyJarMinimum(cart, document.querySelector(".cart-drawer"));
  }


  /* ----------------------------------------------------------
     Cart page render (/cart)
     ----------------------------------------------------------
     This used to be window.location.reload() after every click: a full document load - fonts,
     every section, every image - to change one number. Same data, patched in place instead. */
  function renderCartPage(cart) {
    var list = document.getElementById("cart-items-list");
    if (!list) return;
    var page = document.querySelector(".main-cart");

    if (cart.item_count === 0) {
      /* Nothing left to patch, and the empty state is a different layout - let the server draw it. */
      window.location.reload();
      return;
    }

    var rows = list.querySelectorAll(".cart-item");
    var byKey = Object.create(null);
    cartLines(cart.items).forEach(function (it) { byKey[it.key] = it; });

    rows.forEach(function (row) {
      var key = row.getAttribute("data-item-key");
      var it = byKey[key];
      if (!it) {
        /* Removed. Collapse it rather than reloading the page out from under them. */
        row.classList.add("is-removing");
        setTimeout(function () { if (row.parentNode) row.parentNode.removeChild(row); }, 320);
        return;
      }
      if (isPending(key)) return;   /* shopper is ahead of this response - do not fight them */
      var num = row.querySelector("[data-cart-qty]");
      var price = row.querySelector("[data-cart-line-price]");
      if (num) num.textContent = it.quantity;
      if (price) price.innerHTML = linePriceHtml(it);
      row.classList.remove("is-syncing");
    });

    var count = document.querySelector("[data-cart-items-count]");
    if (count) count.textContent = "Items (" + cart.item_count + ")";
    var itemsSub = document.querySelector("[data-cart-items-subtotal]");
    if (itemsSub) itemsSub.textContent = money(cart.items_subtotal_price);
    var total = document.querySelector("[data-cart-total]");
    if (total) total.textContent = money(cart.total_price);

    /* Free-shipping note + bar. Threshold rides in a data attribute so the market-aware Liquid
       value stays the single source (see sections/main-cart.liquid). */
    var note = document.querySelector("[data-cart-ship-note]");
    if (note) {
      var cents = Number(note.getAttribute("data-fs-threshold")) || 0;
      var flat = note.getAttribute("data-flat-text") || "";
      if (cents > 0 && cart.total_price < cents) {
        var left = (cents - cart.total_price) / 100;
        note.innerHTML = "Add $" + left.toFixed(2) + " more for free shipping on orders over $" +
          (cents / 100) + (flat ? ', <span class="cart-ship__flat">' + escapeHtml(flat) + "</span>" : "") + ".";
      } else {
        note.innerHTML = "You qualify for <strong>free shipping</strong>!";
      }
      var bar = document.querySelector(".main-cart .cart-ship__fill");
      if (bar) bar.style.width = (cents > 0 ? Math.min(100, Math.round(cart.total_price * 100 / cents)) : 0) + "%";
    }

    applyJarMinimum(cart, page);
  }

  function render(cart) {
    updateCounts(cart.item_count);
    renderDrawer(cart);
    renderCartPage(cart);
    return cart;
  }

  function updateCounts(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.classList.add("pop");
      setTimeout(function () { el.classList.remove("pop"); }, 260);
    });
  }

  /* Loose-jar minimum (#78). Jars added from the Bundle Builder carry _bundle_jar, so we can
     count them without guessing from titles - packs and merch never trip this. Runs on every
     render, on both surfaces. `scope` limits it to the cart page when called from there. */
  function applyJarMinimum(cart, scope) {
    var root = scope || document;
    var msg = root.querySelector("[data-cart-min]");
    var btn = root.querySelector("[data-checkout]");
    if (!msg && !btn) return;
    var min = msg ? (Number(msg.getAttribute("data-cart-min")) || 0) : 0;
    var jars = 0;
    (cart.items || []).forEach(function (it) {
      var p = it.properties || {};
      if (p._bundle_jar) { jars += it.quantity; }
    });
    var short = (jars > 0 && jars < min) ? min - jars : 0;
    if (msg) {
      msg.hidden = short === 0;
      msg.textContent = "Jars are sold in " + min + "s - add " + short + " more to check out.";
    }
    if (btn) {
      btn.classList.toggle("is-disabled", short > 0);
      btn.setAttribute("aria-disabled", short > 0 ? "true" : "false");
      if (btn.tagName === "A") {
        /* The cart page's checkout is a link, and `disabled` means nothing to one. Removing the
           href is what actually stops it being followed (matches the Liquid in main-cart.liquid). */
        if (short > 0) { btn.removeAttribute("href"); } else { btn.setAttribute("href", "/checkout"); }
      } else {
        btn.disabled = short > 0;
      }
    }
  }

  function addAndOpen(form, btn) {
    var fd = new FormData(form);
    if (btn) btn.classList.add("is-loading");
    fetch("/cart/add.js", { method: "POST", headers: { "Accept": "application/json" }, body: fd })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (btn) btn.classList.remove("is-loading");
        if (!res.ok) { console.warn("Add to cart failed:", res.data && res.data.description); return; }
        return getCart().then(render).then(function () {
          if (window.SN && window.SN.openCart) window.SN.openCart();
        });
      })
      .catch(function (e) { if (btn) btn.classList.remove("is-loading"); console.error(e); });
  }

  /* Intercept any add-to-cart form (PDP, collection quick-add, featured bundle) */
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute("action") || "";
    if (action.indexOf("/cart/add") > -1) {
      e.preventDefault();
      addAndOpen(form, form.querySelector("[type=submit]"));
    }
  });

  /* ----------------------------------------------------------
     Quantity / remove clicks - drawer and cart page share this
     ---------------------------------------------------------- */
  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;

    /* Blocked checkout (cart page uses an <a>, which ignores `disabled`) */
    var dead = e.target.closest("a[data-checkout].is-disabled, a.cart-summary__checkout.is-disabled");
    if (dead) { e.preventDefault(); return; }

    /* Remove. The request goes out immediately and the animation plays over the top of it -
       waiting 650ms for the animation to finish before starting the fetch made every removal
       feel like the site had stalled. */
    var rm = e.target.closest("[data-cart-remove]");
    if (rm) {
      e.preventDefault();
      var row = rm.closest(".cart-line, .cart-item");
      var rmKey = rm.getAttribute("data-cart-key");
      if (!row || row.classList.contains("is-removing")) { if (rmKey) queueChange(rmKey, 0); return; }
      row.classList.add("is-removing");
      queueChange(rmKey, 0);
      return;
    }

    var b = e.target.closest("[data-cart-delta]");
    if (!b) return;
    e.preventDefault();
    var key = b.getAttribute("data-cart-key");
    var lineRow = b.closest(".cart-line, .cart-item");
    if (!key || !lineRow) return;
    var next = bumpLine(lineRow, Number(b.getAttribute("data-cart-delta")), "[data-cart-qty]");
    if (next === null) return;
    if (next === 0) { lineRow.classList.add("is-removing"); }
    queueChange(key, next);
  });

  /* Upsell carousel: dots + prev/next arrows + autoplay (pauses on hover; respects reduced-motion) */
  function initUpsellCarousel() {
    var track = document.querySelector("[data-upsell-track]");
    if (!track) return;
    var cards = track.children;
    if (cards.length < 2) return;
    var dotsWrap = document.querySelector("[data-upsell-dots]");
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.children) : [];
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var idx = 0, timer = null;

    function sync() {
      var w = cards[0].offsetWidth || 1;
      idx = Math.round(track.scrollLeft / w);
      dots.forEach(function (d, i) { d.classList.toggle("is-active", i === idx); });
    }
    function goTo(i) {
      idx = (i + cards.length) % cards.length;
      track.scrollTo({ left: cards[idx].offsetWidth * idx, behavior: reduce ? "auto" : "smooth" });
    }
    function start() { if (!reduce && !timer) { timer = setInterval(function () { goTo(idx + 1); }, 4500); } }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function reset() { stop(); start(); }

    track.addEventListener("scroll", sync);
    dots.forEach(function (d, i) { d.addEventListener("click", function () { goTo(i); reset(); }); });
    var prev = document.querySelector("[data-upsell-prev]");
    var next = document.querySelector("[data-upsell-next]");
    if (prev) { prev.addEventListener("click", function () { goTo(idx - 1); reset(); }); }
    if (next) { next.addEventListener("click", function () { goTo(idx + 1); reset(); }); }
    var box = document.querySelector(".cart-upsell");
    if (box) { box.addEventListener("mouseenter", stop); box.addEventListener("mouseleave", start); }
    start();
  }

  /* On load: reconcile against /cart.js. Storefront HTML can be served from cache, so the
     server-rendered cart is not always current - that is why this fetch exists and it stays.
     What changed is the cost of it: the controls are already live from the server markup (same
     keys and deltas), and render() now patches instead of rebuilding, so when the HTML is
     accurate - the usual case - this settles into a handful of textContent writes and touches
     no images at all. It used to rebuild the whole list and re-request every full-size photo. */
  document.addEventListener("DOMContentLoaded", function () {
    if (document.querySelector(".cart-drawer__items, #cart-items-list")) {
      getCart().then(render).catch(function () {});
    }
    initUpsellCarousel();
  });
})();
